import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router';
import type { Entitlement, HighlightJob } from '../api/types';
import {
  applyAddonMix,
  applyWatermarkOptIn,
  getJob,
  getMusicManifest,
  removeMusicAddon,
  removeVoiceAddon,
  storeMusicAddon,
  storeVoiceAddon,
} from '../api/rttsClient';
import {
  MIX_POLL_INTERVAL_MS,
  MIX_POLL_MAX_MS,
  STUDIO_COPY,
  WATERMARK_POLL_INTERVAL_MS,
  WATERMARK_POLL_MAX_MS,
} from '../config';
import {
  DEFAULT_MUSIC_VOLUME,
  DEFAULT_REEL_AUDIO_VOLUME,
  DEFAULT_VOICE_VOLUME,
  initialMusicVolume,
  initialReelAudioVolume,
  initialVoiceVolume,
  isMixVersion,
  volumePercentToLinear,
} from '../music/addonVolume';
import { fetchMusicPreviewObjectUrl } from '../music/previewAudio';
import {
  alignRecordedVoiceBlob,
  formatMss,
  formatRecordTimer,
  probeBlobDurationSec,
  recordProgressPercent,
  restoreVideoPlayback,
  snapshotVideoPlayback,
  startSyncedCapture,
  type VideoPlaybackSnapshot,
} from '../music/recordToPicture';
import {
  isVoiceRecordingSupported,
  pickSupportedRecorderMime,
} from '../music/recorderMime';
import type { MusicTrack } from '../music/types';
import { isUnlimitedEntitlement } from '../utils/vodEligibility';

export type AddOnsInspectorSection = 'mix' | 'music' | 'voice' | 'branding';

export type AddonMixVolumes = {
  music_volume: number;
  reel_audio_volume: number;
  voice_volume: number;
};

type AddOnsPanelProps = {
  job: HighlightJob;
  entitlement: Entitlement | null;
  onJobUpdated: (job: HighlightJob) => void;
  /** Studio reel player — record-to-picture seeks/plays this element. */
  videoRef?: React.RefObject<HTMLVideoElement | null>;
  /** When set, recording transport (timer / Stop / progress) portals here. */
  recordDock?: HTMLElement | null;
  /** Studio reel layout pins the player while a take is in progress. */
  onRecordingChange?: (recording: boolean) => void;
  /** V2 inspector: mount one existing section. Omit for the full panel. */
  inspectorSection?: AddOnsInspectorSection;
  /** V2 Audio Mix: live slider values for the shared preview mix-state. */
  onMixVolumesChange?: (next: AddonMixVolumes) => void;
};

type VoiceTake = {
  blob: Blob;
  url: string;
  durationSec: number;
  offsetMs: number;
};

const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

type PollOutcome = 'ready' | 'failed' | 'applying';

async function pollJobStatus(opts: {
  jobId: string;
  deadline: number;
  intervalMs: number;
  readStatus: (job: HighlightJob) => string | null | undefined;
  onJob: (job: HighlightJob) => void;
  isCancelled?: () => boolean;
}): Promise<PollOutcome> {
  let first = true;
  while (Date.now() < opts.deadline) {
    if (opts.isCancelled?.()) return 'applying';
    if (!first) await sleep(opts.intervalMs);
    first = false;
    try {
      const { job: polled } = await getJob(opts.jobId);
      if (opts.isCancelled?.()) return 'applying';
      opts.onJob(polled);
      const status = String(opts.readStatus(polled) || '');
      if (status === 'ready') return 'ready';
      if (status === 'failed') return 'failed';
    } catch {
      // Transient getJob errors are not an operation failure — retry.
    }
  }
  return 'applying';
}

const isPro = (entitlement: Entitlement | null): boolean => {
  if (!entitlement) return false;
  if (isUnlimitedEntitlement(entitlement)) return true;
  const tier = entitlement.tier;
  return tier === 'paid' || tier === 'admin' || tier === 'comped';
};

const MixSlider: React.FC<{
  label: string;
  value: number;
  disabled?: boolean;
  testId: string;
  mutedLabel?: string;
  caption?: string;
  onChange: (next: number) => void;
}> = ({ label, value, disabled, testId, mutedLabel, caption, onChange }) => {
  const muted = value <= 0 && Boolean(mutedLabel);
  return (
    <label className={`studio-addons-slider${muted ? ' is-muted' : ''}`}>
      <span className="studio-addons-slider-head">
        <span className="studio-addons-slider-copy">
          <span className="studio-addons-slider-label">{label}</span>
          {caption ? (
            <span className="studio-addons-slider-caption">{caption}</span>
          ) : null}
        </span>
        <span className="studio-addons-slider-chip">
          <span className="studio-addons-slider-pct" data-testid={`${testId}-pct`}>
            {value}%
            {muted ? (
              <span
                className="studio-addons-slider-muted"
                data-testid={`${testId}-muted`}
              >
                {mutedLabel}
              </span>
            ) : null}
          </span>
        </span>
      </span>
      <input
        type="range"
        min={0}
        max={100}
        step={1}
        value={value}
        disabled={disabled}
        data-testid={testId}
        aria-label={label}
        aria-valuetext={muted ? `${value}% ${mutedLabel}` : `${value}%`}
        style={{
          background: `linear-gradient(to right, #7c3aed ${value}%, rgba(255,255,255,0.12) ${value}%)`,
        }}
        onChange={(event) => onChange(Number(event.target.value))}
      />
    </label>
  );
};

const mixSummary = (job: HighlightJob): string => {
  const music = Boolean(job.music_id) || job.music_source === 'uploaded' || Boolean(job.music_asset_id);
  const voice = Boolean(job.voice_asset_id);
  if (music && voice) return STUDIO_COPY.addonsSummaryBoth;
  if (voice) return STUDIO_COPY.addonsSummaryVoice;
  return STUDIO_COPY.addonsSummaryMusic;
};

/** True when the stored branding choice is on — missing is not treated as off during overlay. */
const watermarkOptInOn = (job: HighlightJob): boolean => {
  if (job.watermark_opt_in === true) return true;
  if (job.watermark_opt_in === false) return false;
  const status = job.watermark_status;
  return status === 'applying' || status === 'ready' || status === 'failed';
};

const uploadNotice = (job: HighlightJob): string | null => {
  const uploadedVoice = job.voice_source === 'uploaded';
  const uploadedMusic =
    job.music_source === 'uploaded' ||
    (Boolean(job.music_asset_id) && job.music_source !== 'library');
  if (uploadedVoice && uploadedMusic) return STUDIO_COPY.addonsUploadOnly;
  if (uploadedMusic) return STUDIO_COPY.addonsMusicUploadOnly;
  if (uploadedVoice) return STUDIO_COPY.addonsVoiceUploadOnly;
  return null;
};

const AddOnsPanel: React.FC<AddOnsPanelProps> = ({
  job,
  entitlement,
  onJobUpdated,
  videoRef,
  recordDock = null,
  onRecordingChange,
  inspectorSection,
  onMixVolumesChange,
}) => {
  const showAll = !inspectorSection;
  const showMix = showAll || inspectorSection === 'mix';
  const showMusic = showAll || inspectorSection === 'music';
  const showVoice = showAll || inspectorSection === 'voice';
  const showBranding = showAll || inspectorSection === 'branding';
  const [tracks, setTracks] = useState<MusicTrack[]>([]);
  const [selectedId, setSelectedId] = useState(job.music_id || '');
  const [musicVolume, setMusicVolume] = useState(() => initialMusicVolume(job));
  const [reelAudioVolume, setReelAudioVolume] = useState(() =>
    initialReelAudioVolume(job)
  );
  const [voiceVolume, setVoiceVolume] = useState(() => initialVoiceVolume(job));
  const mixVolumesRef = useRef<AddonMixVolumes>({
    music_volume: musicVolume,
    reel_audio_volume: reelAudioVolume,
    voice_volume: voiceVolume,
  });
  mixVolumesRef.current = {
    music_volume: musicVolume,
    reel_audio_volume: reelAudioVolume,
    voice_volume: voiceVolume,
  };
  const setMixVolume = (patch: Partial<AddonMixVolumes>) => {
    const next = { ...mixVolumesRef.current, ...patch };
    if (patch.music_volume != null) setMusicVolume(next.music_volume);
    if (patch.reel_audio_volume != null) setReelAudioVolume(next.reel_audio_volume);
    if (patch.voice_volume != null) setVoiceVolume(next.voice_volume);
    onMixVolumesChange?.(next);
  };
  const [busy, setBusy] = useState(false);
  const [wmBusy, setWmBusy] = useState(false);
  const [wmApplying, setWmApplying] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [workingHint, setWorkingHint] = useState<string | null>(null);
  const [auditionId, setAuditionId] = useState<string | null>(null);
  const [recording, setRecording] = useState(false);
  const [recordUnavailable, setRecordUnavailable] = useState(
    () => !isVoiceRecordingSupported()
  );
  const [previewing, setPreviewing] = useState(false);
  const [monitorEnabled, setMonitorEnabled] = useState(false);
  const [recordElapsed, setRecordElapsed] = useState(0);
  const [recordTotal, setRecordTotal] = useState(0);
  const [voiceTake, setVoiceTake] = useState<VoiceTake | null>(null);
  const [rightsAck, setRightsAck] = useState(false);
  const [collapsed, setCollapsed] = useState(() =>
    isMixVersion(job.reel_active_version)
  );
  const auditionRef = useRef<HTMLAudioElement | null>(null);
  const recorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const uploadRef = useRef<HTMLInputElement | null>(null);
  const musicUploadRef = useRef<HTMLInputElement | null>(null);
  const recordingRef = useRef(false);
  const previewingRef = useRef(false);
  const captureRestoreRef = useRef<VideoPlaybackSnapshot | null>(null);
  const offsetMsRef = useRef(0);
  const rafRef = useRef<number | null>(null);
  const previewVoiceRef = useRef<HTMLAudioElement | null>(null);
  const previewMusicRef = useRef<HTMLAudioElement | null>(null);
  const takeUrlRef = useRef<string | null>(null);
  const wmPollLock = useRef(false);
  const wmPollGaveUp = useRef(false);
  const mixPollLock = useRef(false);
  const confirmedAddedRef = useRef(job.watermark_opt_in === true);
  const onRecordingChangeRef = useRef(onRecordingChange);
  onRecordingChangeRef.current = onRecordingChange;
  const onJobUpdatedRef = useRef(onJobUpdated);
  onJobUpdatedRef.current = onJobUpdated;
  const pro = isPro(entitlement);
  const mixOn = isMixVersion(job.reel_active_version);
  const hasVoice = Boolean(job.voice_asset_id);
  const uploadedMusic =
    job.music_source === 'uploaded' ||
    (Boolean(job.music_asset_id) && job.music_source !== 'library');
  const uploadsEnabled = entitlement?.upload_addons_enabled === true;
  const canApply = Boolean(selectedId || hasVoice || uploadedMusic || voiceTake);
  const notice = uploadNotice(job);
  const reelDurationSec =
    (videoRef?.current && Number.isFinite(videoRef.current.duration)
      ? videoRef.current.duration
      : 0) ||
    (typeof job.reel_duration_sec === 'number' ? job.reel_duration_sec : 0) ||
    recordTotal;

  useEffect(() => {
    const uploaded =
      job.music_source === 'uploaded' ||
      (Boolean(job.music_asset_id) && job.music_source !== 'library');
    if (uploaded) {
      setSelectedId('');
      return;
    }
    const persisted = (job.music_id || '').trim();
    if (persisted) setSelectedId(persisted);
  }, [job.music_id, job.music_source, job.music_asset_id]);

  useEffect(() => {
    setMusicVolume(initialMusicVolume(job));
    setReelAudioVolume(initialReelAudioVolume(job));
  }, [job.music_id, job.music_volume, job.reel_audio_volume, job.music_addon_level]);

  useEffect(() => {
    setVoiceVolume(initialVoiceVolume(job));
  }, [job.voice_volume]);

  useEffect(() => {
    setCollapsed(isMixVersion(job.reel_active_version));
  }, [job.id]);

  useEffect(() => {
    if (job.watermark_opt_in === true && job.watermark_status === 'ready') {
      confirmedAddedRef.current = true;
    }
    if (job.watermark_opt_in === false) {
      confirmedAddedRef.current = false;
    }
  }, [job.watermark_opt_in, job.watermark_status]);

  useEffect(() => {
    if (job.watermark_status === 'ready' || job.watermark_status === 'failed') {
      wmPollGaveUp.current = false;
    }
    if (
      !job.id ||
      job.watermark_status !== 'applying' ||
      wmPollLock.current ||
      wmPollGaveUp.current
    ) {
      return undefined;
    }
    let cancelled = false;
    wmPollLock.current = true;
    setWmBusy(true);
    setWmApplying(true);
    void (async () => {
      const deadline = Date.now() + WATERMARK_POLL_MAX_MS;
      try {
        const outcome = await pollJobStatus({
          jobId: job.id,
          deadline,
          intervalMs: WATERMARK_POLL_INTERVAL_MS,
          readStatus: (polled) => polled.watermark_status,
          onJob: (polled) => onJobUpdatedRef.current(polled),
          isCancelled: () => cancelled,
        });
        if (cancelled) return;
        if (outcome === 'ready') {
          setWorkingHint(null);
          setError(null);
          wmPollGaveUp.current = false;
          return;
        }
        if (outcome === 'failed') {
          setWorkingHint(null);
          setError(STUDIO_COPY.addonsWatermarkFailed);
          wmPollGaveUp.current = false;
          return;
        }
        wmPollGaveUp.current = true;
        setError(null);
        setWorkingHint(STUDIO_COPY.addonsWatermarkStillWorking);
      } finally {
        wmPollLock.current = false;
        setWmBusy(false);
        setWmApplying(false);
      }
    })();
    return () => {
      cancelled = true;
      wmPollLock.current = false;
    };
  }, [job.id, job.watermark_status]);

  useEffect(() => {
    if (!job.id || job.mix_status !== 'applying' || mixPollLock.current) {
      return undefined;
    }
    let cancelled = false;
    mixPollLock.current = true;
    setBusy(true);
    void (async () => {
      const deadline = Date.now() + MIX_POLL_MAX_MS;
      try {
        const outcome = await pollJobStatus({
          jobId: job.id,
          deadline,
          intervalMs: MIX_POLL_INTERVAL_MS,
          readStatus: (polled) => polled.mix_status,
          onJob: (polled) => onJobUpdatedRef.current(polled),
          isCancelled: () => cancelled,
        });
        if (cancelled) return;
        if (outcome === 'ready') {
          setWorkingHint(null);
          setError(null);
          return;
        }
        if (outcome === 'failed') {
          setWorkingHint(null);
          setError(STUDIO_COPY.addonsFailed);
          return;
        }
        setError(null);
        setWorkingHint(STUDIO_COPY.addonsMixStillWorking);
      } finally {
        mixPollLock.current = false;
        setBusy(false);
      }
    })();
    return () => {
      cancelled = true;
      mixPollLock.current = false;
    };
  }, [job.id, job.mix_status]);

  useEffect(() => {
    let cancelled = false;
    void getMusicManifest()
      .then((payload) => {
        if (!cancelled) setTracks(payload.tracks);
      })
      .catch(() => {
        if (!cancelled) setTracks([]);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    onRecordingChangeRef.current?.(recording);
    document.documentElement.classList.toggle('studio-is-recording', recording);
    return () => {
      document.documentElement.classList.remove('studio-is-recording');
    };
  }, [recording]);

  const stopAudition = () => {
    const audio = auditionRef.current;
    if (audio) {
      try {
        audio.pause();
        audio.removeAttribute('src');
        audio.load();
      } catch {
        // jsdom / autoplay — ignore
      }
    }
    setAuditionId(null);
  };

  useEffect(() => () => {
    stopAudition();
    stopTimerLoop();
    clearTakeUrl();
  }, []);

  const clearTakeUrl = () => {
    if (takeUrlRef.current) {
      URL.revokeObjectURL(takeUrlRef.current);
      takeUrlRef.current = null;
    }
  };

  const stopTimerLoop = () => {
    if (rafRef.current != null) {
      window.cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    }
  };

  const stopPreview = () => {
    previewingRef.current = false;
    setPreviewing(false);
    const voice = previewVoiceRef.current;
    const music = previewMusicRef.current;
    previewVoiceRef.current = null;
    previewMusicRef.current = null;
    if (voice) {
      try {
        voice.pause();
        voice.removeAttribute('src');
        voice.load();
      } catch {
        // jsdom
      }
    }
    if (music) {
      try {
        music.pause();
      } catch {
        // jsdom
      }
    }
    const video = videoRef?.current;
    if (video) restoreVideoPlayback(video, captureRestoreRef.current);
  };

  const tickCapture = () => {
    if (!recordingRef.current) return;
    const video = videoRef?.current;
    const duration =
      (video && Number.isFinite(video.duration) && video.duration > 0
        ? video.duration
        : 0) ||
      (typeof job.reel_duration_sec === 'number' ? job.reel_duration_sec : 0) ||
      0;
    const elapsed =
      video && Number.isFinite(video.currentTime) ? video.currentTime : 0;
    setRecordElapsed(elapsed);
    setRecordTotal(duration);
    if (duration > 0 && elapsed >= duration - 0.04) {
      stopRecording();
      return;
    }
    rafRef.current = window.requestAnimationFrame(tickCapture);
  };

  const startTimerLoop = () => {
    stopTimerLoop();
    rafRef.current = window.requestAnimationFrame(tickCapture);
  };

  const togglePreview = async (track: MusicTrack, event: React.MouseEvent) => {
    event.stopPropagation();
    if (!pro) return;
    if (auditionId === track.id) {
      stopAudition();
      return;
    }
    stopAudition();
    try {
      const url = await fetchMusicPreviewObjectUrl(track.id, {
        previewUrl: track.preview_url,
      });
      const audio = auditionRef.current || new Audio();
      auditionRef.current = audio;
      audio.src = url;
      await audio.play();
      setAuditionId(track.id);
    } catch {
      setAuditionId(null);
    }
  };

  const stopRecording = () => {
    const rec = recorderRef.current;
    recordingRef.current = false;
    stopTimerLoop();
    recorderRef.current = null;
    setRecording(false);
    if (rec) {
      try {
        rec.stop();
      } catch {
        if (typeof rec.onstop === 'function') rec.onstop(new Event('stop'));
      }
    }
    const video = videoRef?.current;
    if (video) {
      try {
        video.pause();
      } catch {
        // jsdom
      }
      if (!previewingRef.current) {
        restoreVideoPlayback(video, captureRestoreRef.current);
      }
    }
  };

  const startRecording = async () => {
    if (!job.id || busy || !navigator.mediaDevices?.getUserMedia) return;
    if (!isVoiceRecordingSupported()) {
      setRecordUnavailable(true);
      setError(STUDIO_COPY.addonsVoiceUnsupported);
      return;
    }
    stopAudition();
    stopPreview();
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mime = pickSupportedRecorderMime();
      let recorder: MediaRecorder;
      try {
        recorder = mime
          ? new MediaRecorder(stream, { mimeType: mime })
          : new MediaRecorder(stream);
      } catch {
        stream.getTracks().forEach((track) => track.stop());
        setRecordUnavailable(true);
        setError(STUDIO_COPY.addonsVoiceUnsupported);
        return;
      }
      const recType = recorder.mimeType || mime || 'audio/webm';
      chunksRef.current = [];
      recorder.ondataavailable = (event) => {
        if (event.data && event.data.size > 0) chunksRef.current.push(event.data);
      };
      recorder.onstop = () => {
        stream.getTracks().forEach((track) => track.stop());
        const raw = new Blob(chunksRef.current, { type: recType });
        chunksRef.current = [];
        if (!job.id || raw.size < 1) return;
        setBusy(true);
        setError(null);
        void (async () => {
          const aligned = await alignRecordedVoiceBlob(raw, offsetMsRef.current);
          const durationSec = await probeBlobDurationSec(aligned.blob);
          const result = await storeVoiceAddon(job.id, aligned.blob, {
            voice_source: 'recorded',
          });
          onJobUpdated(result.job);
          clearTakeUrl();
          let url = '';
          try {
            url = URL.createObjectURL(aligned.blob);
          } catch {
            url = '';
          }
          takeUrlRef.current = url || null;
          setVoiceTake({
            blob: aligned.blob,
            url,
            durationSec,
            offsetMs: aligned.offsetMs,
          });
        })()
          .catch(() => {
            setError(STUDIO_COPY.addonsFailed);
          })
          .finally(() => {
            setBusy(false);
            setRecording(false);
            recordingRef.current = false;
          });
      };
      recorderRef.current = recorder;
      const video = videoRef?.current ?? null;
      if (video) {
        captureRestoreRef.current = snapshotVideoPlayback(video);
        video.addEventListener(
          'ended',
          () => {
            if (recordingRef.current) stopRecording();
          },
          { once: true }
        );
      }
      recordingRef.current = true;
      setRecording(true);
      setRecordElapsed(0);
      setRecordTotal(
        (video && Number.isFinite(video.duration) && video.duration > 0
          ? video.duration
          : 0) ||
          (typeof job.reel_duration_sec === 'number' ? job.reel_duration_sec : 0) ||
          0
      );
      const sync = await startSyncedCapture({
        video,
        recorder,
        muteReel: !monitorEnabled,
      });
      offsetMsRef.current = sync.offsetMs;
      startTimerLoop();
    } catch {
      setError(STUDIO_COPY.addonsFailed);
      recordingRef.current = false;
      setRecording(false);
      const video = videoRef?.current;
      if (video) restoreVideoPlayback(video, captureRestoreRef.current);
    }
  };

  const setMonitor = (enabled: boolean) => {
    setMonitorEnabled(enabled);
    if (!recordingRef.current) return;
    const video = videoRef?.current;
    if (video) video.muted = !enabled;
  };

  const playTakePreview = async () => {
    const take = voiceTake;
    const video = videoRef?.current;
    if (!take || !video || busy || recordingRef.current) return;
    stopAudition();
    stopPreview();
    captureRestoreRef.current = snapshotVideoPlayback(video);
    video.controls = false;
    // Baked voiceover lives on the reel <video> audio. Mute it during take
    // preview so the new take + selected music do not mash with the old bake.
    // Game bed is lost for this preview when hasVoice; music-only audition is
    // unchanged (stopAudition / playAudition).
    video.muted = hasVoice;
    if (!hasVoice) {
      video.volume = volumePercentToLinear(reelAudioVolume, DEFAULT_REEL_AUDIO_VOLUME);
    }
    try {
      video.currentTime = 0;
    } catch {
      // jsdom
    }
    const voice = new Audio(take.url);
    voice.volume = volumePercentToLinear(voiceVolume, DEFAULT_VOICE_VOLUME);
    previewVoiceRef.current = voice;
    let music: HTMLAudioElement | null = null;
    if (selectedId) {
      try {
        const track = tracks.find((item) => item.id === selectedId);
        const url = await fetchMusicPreviewObjectUrl(selectedId, {
          previewUrl: track?.preview_url,
        });
        music = new Audio(url);
        music.volume = volumePercentToLinear(musicVolume, DEFAULT_MUSIC_VOLUME);
        previewMusicRef.current = music;
      } catch {
        music = null;
      }
    }
    const stopAll = () => {
      if (previewingRef.current) stopPreview();
    };
    video.onended = stopAll;
    voice.onended = stopAll;
    previewingRef.current = true;
    setPreviewing(true);
    try {
      const plays = [video.play(), voice.play()];
      if (music) plays.push(music.play());
      await Promise.all(plays);
    } catch {
      stopPreview();
    }
  };

  const clearTake = async () => {
    if (busy) return;
    stopPreview();
    stopRecording();
    clearTakeUrl();
    setVoiceTake(null);
    if (!job.id || !hasVoice) return;
    setBusy(true);
    setError(null);
    try {
      const result = await removeVoiceAddon(job.id);
      onJobUpdated(result.job);
    } catch {
      setError(STUDIO_COPY.addonsFailed);
    } finally {
      setBusy(false);
    }
  };

  const bakeMix = async (closeAfter: boolean) => {
    if (!job.id || busy || !canApply) return;
    setBusy(true);
    setError(null);
    setWorkingHint(null);
    stopAudition();
    stopPreview();
    mixPollLock.current = true;
    try {
      const dropMusic = !selectedId && !uploadedMusic;
      const result = await applyAddonMix(job.id, {
        music_id: dropMusic ? undefined : selectedId || undefined,
        music_volume: musicVolume,
        reel_audio_volume: reelAudioVolume,
        voice_volume: voiceVolume,
        ...(dropMusic && (hasVoice || voiceTake) ? { clear_music: true } : {}),
      });
      onJobUpdated(result.job);
      if (result.job.mix_status === 'applying') {
        const outcome = await pollJobStatus({
          jobId: job.id,
          deadline: Date.now() + MIX_POLL_MAX_MS,
          intervalMs: MIX_POLL_INTERVAL_MS,
          readStatus: (polled) => polled.mix_status,
          onJob: onJobUpdated,
        });
        if (outcome === 'ready') {
          setWorkingHint(null);
          if (closeAfter) setCollapsed(true);
          return;
        }
        if (outcome === 'failed') {
          setWorkingHint(null);
          setError(STUDIO_COPY.addonsFailed);
          return;
        }
        setWorkingHint(STUDIO_COPY.addonsMixStillWorking);
        return;
      }
      if (closeAfter) setCollapsed(true);
    } catch (err) {
      const code =
        err && typeof err === 'object' && 'error' in err
          ? String((err as { error: string }).error)
          : '';
      setError(
        code === 'upgrade_required'
          ? STUDIO_COPY.addonsLocked
          : code === 'music_addon_cooldown'
            ? STUDIO_COPY.addonsCooldown
            : STUDIO_COPY.addonsFailed
      );
    } finally {
      mixPollLock.current = false;
      setBusy(false);
    }
  };

  const toggleWatermark = async () => {
    if (!job.id || busy || wmBusy) return;
    const enable =
      job.watermark_status === 'failed' || job.watermark_opt_in !== true;
    setWmBusy(true);
    setError(null);
    setWorkingHint(null);
    wmPollLock.current = true;
    if (enable) setWmApplying(true);
    try {
      const result = await applyWatermarkOptIn(job.id, enable);
      onJobUpdated(result.job);
      if (enable && result.job.watermark_status === 'applying') {
        const outcome = await pollJobStatus({
          jobId: job.id,
          deadline: Date.now() + WATERMARK_POLL_MAX_MS,
          intervalMs: WATERMARK_POLL_INTERVAL_MS,
          readStatus: (polled) => polled.watermark_status,
          onJob: onJobUpdated,
        });
        if (outcome === 'ready') {
          setWorkingHint(null);
          wmPollGaveUp.current = false;
          return;
        }
        if (outcome === 'failed') {
          setWorkingHint(null);
          wmPollGaveUp.current = false;
          setError(STUDIO_COPY.addonsWatermarkFailed);
          return;
        }
        wmPollGaveUp.current = true;
        setWorkingHint(STUDIO_COPY.addonsWatermarkStillWorking);
      }
    } catch {
      setError(STUDIO_COPY.addonsWatermarkFailed);
    } finally {
      wmPollLock.current = false;
      setWmBusy(false);
      setWmApplying(false);
    }
  };

  const watermarkOptInEnabled = Boolean(entitlement?.watermark_opt_in_enabled);
  const watermarkOn = watermarkOptInOn(job);
  const watermarkApplying = wmApplying;
  const watermarkAdded =
    watermarkOn && job.watermark_status === 'ready' && !watermarkApplying;
  const watermarkState = watermarkApplying
    ? 'applying'
    : watermarkAdded
      ? 'added'
      : 'off';
  const watermarkTitle = watermarkApplying
    ? confirmedAddedRef.current
      ? STUDIO_COPY.addonsWatermarkReapplying
      : STUDIO_COPY.addonsWatermarkApplying
    : watermarkAdded
      ? STUDIO_COPY.addonsWatermarkOn
      : STUDIO_COPY.addonsWatermark;
  const watermarkSummaryCopy = watermarkApplying
    ? confirmedAddedRef.current
      ? STUDIO_COPY.addonsWatermarkReapplying
      : STUDIO_COPY.addonsWatermarkApplying
    : watermarkAdded
      ? STUDIO_COPY.addonsWatermarkOn
      : null;
  const watermarkToggle =
    pro && watermarkOptInEnabled ? (
      <label
        className={`studio-addons-watermark${watermarkOn ? ' is-on' : ''}${
          watermarkApplying ? ' is-applying' : ''
        }${watermarkAdded ? ' is-added' : ''}`}
        data-testid="studio-addons-watermark"
        data-state={watermarkState}
      >
        <input
          type="checkbox"
          checked={watermarkOn}
          disabled={busy || wmBusy}
          data-testid="studio-addons-watermark-toggle"
          aria-label={watermarkTitle}
          onChange={() => void toggleWatermark()}
        />
        <span>
          <span className="studio-addons-watermark-title">
            {watermarkTitle}
            {watermarkAdded ? (
              <span className="studio-addons-watermark-pill" data-testid="studio-addons-watermark-pill">
                {STUDIO_COPY.addonsWatermarkOnPill}
              </span>
            ) : null}
            {watermarkApplying ? (
              <span
                className="studio-addons-watermark-spinner"
                aria-hidden="true"
                data-testid="studio-addons-watermark-spinner"
              />
            ) : null}
          </span>
          <span className="studio-addons-watermark-copy">
            {STUDIO_COPY.addonsWatermarkBlurb}
          </span>
        </span>
      </label>
    ) : null;

  const onRemove = async () => {
    if (!job.id || busy) return;
    setBusy(true);
    setError(null);
    stopAudition();
    stopPreview();
    stopRecording();
    clearTakeUrl();
    setVoiceTake(null);
    try {
      const result = await removeMusicAddon(job.id);
      onJobUpdated(result.job);
      setMusicVolume(DEFAULT_MUSIC_VOLUME);
      setReelAudioVolume(DEFAULT_REEL_AUDIO_VOLUME);
      setVoiceVolume(DEFAULT_VOICE_VOLUME);
      setRightsAck(false);
      setCollapsed(false);
    } catch {
      setError(STUDIO_COPY.addonsFailed);
    } finally {
      setBusy(false);
    }
  };

  const onUploadFile = async (file: File) => {
    if (!job.id || busy || !rightsAck) return;
    setBusy(true);
    setError(null);
    try {
      const result = await storeVoiceAddon(job.id, file, {
        voice_source: 'uploaded',
        rights_ack: true,
      });
      onJobUpdated(result.job);
    } catch {
      setError(STUDIO_COPY.addonsFailed);
    } finally {
      setBusy(false);
    }
  };

  const onUploadMusic = async (file: File) => {
    if (!job.id || busy || !rightsAck) return;
    setBusy(true);
    setError(null);
    try {
      const result = await storeMusicAddon(job.id, file, { rights_ack: true });
      setSelectedId('');
      if (musicVolume === 0) {
        setMusicVolume(DEFAULT_MUSIC_VOLUME);
      }
      onJobUpdated(result.job);
    } catch {
      setError(STUDIO_COPY.addonsFailed);
    } finally {
      setBusy(false);
    }
  };

  const onRemoveUploadedMusic = async () => {
    if (!job.id || busy) return;
    setBusy(true);
    setError(null);
    setWorkingHint(null);
    stopAudition();
    mixPollLock.current = true;
    try {
      const result = await applyAddonMix(job.id, {
        music_volume: musicVolume,
        reel_audio_volume: reelAudioVolume,
        voice_volume: voiceVolume,
        clear_music: true,
      });
      setSelectedId('');
      onJobUpdated(result.job);
      if (result.job.mix_status === 'applying') {
        const outcome = await pollJobStatus({
          jobId: job.id,
          deadline: Date.now() + MIX_POLL_MAX_MS,
          intervalMs: MIX_POLL_INTERVAL_MS,
          readStatus: (polled) => polled.mix_status,
          onJob: onJobUpdated,
        });
        if (outcome === 'failed') {
          setWorkingHint(null);
          setError(STUDIO_COPY.addonsFailed);
          return;
        }
        if (outcome === 'applying') {
          setWorkingHint(STUDIO_COPY.addonsMixStillWorking);
        } else {
          setWorkingHint(null);
        }
      }
    } catch (err) {
      const code =
        err && typeof err === 'object' && 'error' in err
          ? String((err as { error: string }).error)
          : '';
      setError(
        code === 'music_addon_cooldown'
          ? STUDIO_COPY.addonsCooldown
          : STUDIO_COPY.addonsFailed
      );
    } finally {
      mixPollLock.current = false;
      setBusy(false);
    }
  };

  const dockedRecording = Boolean(recording && recordDock);
  const recordMeter = (
    <div
      className="studio-addons-record-meter"
      data-testid="studio-addons-voice-recording"
      data-record-offset-ms={offsetMsRef.current}
    >
      <span className="studio-addons-record-dot" aria-hidden="true" />
      <span className="studio-addons-record-label">
        {STUDIO_COPY.addonsVoiceRecording}
      </span>
      <span
        className="studio-addons-record-timer"
        data-testid="studio-addons-record-timer"
      >
        {formatRecordTimer(recordElapsed, recordTotal || reelDurationSec)}
      </span>
      <span
        className="studio-addons-record-bar"
        data-testid="studio-addons-record-progress"
      >
        <span
          className="studio-addons-record-bar-fill"
          style={{
            width: `${recordProgressPercent(
              recordElapsed,
              recordTotal || reelDurationSec
            )}%`,
          }}
        />
      </span>
    </div>
  );
  const dockTransport = dockedRecording ? (
    <div
      className="studio-addons-record-meter studio-result-record-transport"
      data-testid="studio-addons-voice-recording"
      data-record-offset-ms={offsetMsRef.current}
    >
      <span className="studio-addons-record-dot" aria-hidden="true" />
      <span className="studio-addons-record-label">
        {STUDIO_COPY.addonsVoiceRecording}
      </span>
      <span
        className="studio-addons-record-timer"
        data-testid="studio-addons-record-timer"
      >
        {formatRecordTimer(recordElapsed, recordTotal || reelDurationSec)}
      </span>
      <button
        type="button"
        className="studio-btn-primary studio-result-record-stop"
        data-testid="studio-addons-voice-stop"
        disabled={busy}
        onClick={() => stopRecording()}
      >
        {STUDIO_COPY.addonsVoiceStop}
      </button>
      <span
        className="studio-addons-record-bar"
        data-testid="studio-addons-record-progress"
      >
        <span
          className="studio-addons-record-bar-fill"
          style={{
            width: `${recordProgressPercent(
              recordElapsed,
              recordTotal || reelDurationSec
            )}%`,
          }}
        />
      </span>
      <label className="studio-addons-monitor">
        <input
          type="checkbox"
          checked={monitorEnabled}
          disabled={busy}
          data-testid="studio-addons-voice-monitor"
          onChange={(event) => setMonitor(event.target.checked)}
        />
        {STUDIO_COPY.addonsVoiceMonitor}
      </label>
    </div>
  ) : null;

  if (collapsed && mixOn && showAll) {
    return (
      <div className="studio-addons studio-addons-full studio-addons-summary" data-testid="studio-addons">
        <div className="studio-addons-card">
        <p className="studio-addons-summary-row">
          <span data-testid="studio-addons-summary">{mixSummary(job)}</span>
          {watermarkSummaryCopy ? (
            <>
              {' · '}
              <span data-testid="studio-addons-watermark-summary">
                {watermarkSummaryCopy}
              </span>
            </>
          ) : null}
          {' · '}
          <button
            type="button"
            className="studio-addons-edit"
            data-testid="studio-addons-edit"
            onClick={() => setCollapsed(false)}
          >
            {STUDIO_COPY.addonsSummaryEdit}
          </button>
        </p>
        {watermarkToggle}
        {error ? (
          <p
            className="studio-addons-error"
            role="alert"
            data-testid="studio-addons-error"
          >
            {error}
          </p>
        ) : null}
        </div>
      </div>
    );
  }

  return (
    <div
      className={`studio-addons${showAll ? ' studio-addons-full' : ''}`}
      data-testid="studio-addons"
    >
      <audio ref={auditionRef} data-testid="studio-addons-audition-audio" />
      {showAll ? (
        <div className="studio-addons-header">
          <h2 className="studio-addons-title">{STUDIO_COPY.addonsTitle}</h2>
          <p className="studio-addons-blurb">{STUDIO_COPY.addonsBlurb}</p>
        </div>
      ) : null}
      {!pro ? (
        <div
          className={`studio-addons-locked${showAll ? ' studio-addons-card' : ''}`}
          data-testid="studio-addons-locked"
        >
          <p>{STUDIO_COPY.addonsLocked}</p>
          <Link
            to="/studio/upgrade"
            className="studio-btn-primary no-underline inline-flex"
            data-testid="studio-addons-upgrade"
          >
            {STUDIO_COPY.addonsUpgradeCta}
          </Link>
          <p className="studio-addons-watermark-free" data-testid="studio-addons-watermark-free">
            {STUDIO_COPY.addonsWatermarkFreeNote}
          </p>
        </div>
      ) : (
        <>
          {showMusic ? (
            <section className={showAll ? 'studio-addons-card' : undefined}>
          <p className="studio-addons-section-label">{STUDIO_COPY.addonsTrack}</p>
          {uploadsEnabled ? (
            <label className="studio-addons-rights">
              <input
                type="checkbox"
                checked={rightsAck}
                data-testid="studio-addons-music-rights"
                onChange={(event) => setRightsAck(event.target.checked)}
              />
              {STUDIO_COPY.addonsVoiceRightsAck}
            </label>
          ) : null}
          <div
            className="studio-addons-tracks"
            role="listbox"
            aria-label={STUDIO_COPY.addonsTrack}
            data-testid="studio-addons-tracks"
          >
            <div
              className={`studio-addons-chip ${!selectedId && !uploadedMusic ? 'is-selected' : ''}`}
              role="option"
              aria-selected={!selectedId && !uploadedMusic}
            >
              <span className="studio-addons-chip-art is-none" aria-hidden="true">
                —
              </span>
              <button
                type="button"
                className="studio-addons-chip-pick"
                data-testid="studio-addons-track-none"
                onClick={() => setSelectedId('')}
              >
                {STUDIO_COPY.musicNone}
              </button>
            </div>
            {tracks.map((track) => {
              const selected = selectedId === track.id;
              const auditioning = auditionId === track.id;
              const durationSec =
                typeof track.duration_s === 'number' && track.duration_s > 0
                  ? track.duration_s
                  : null;
              return (
                <div
                  key={track.id}
                  className={`studio-addons-chip ${selected ? 'is-selected' : ''}`}
                  role="option"
                  aria-selected={selected}
                >
                  <span className="studio-addons-chip-art" aria-hidden="true">
                    {track.display_name.trim().charAt(0).toUpperCase() || '♪'}
                  </span>
                  <button
                    type="button"
                    className="studio-addons-chip-pick"
                    data-testid={`studio-addons-track-${track.id}`}
                    onClick={() => {
                      if (selected) {
                        setSelectedId('');
                        return;
                      }
                      setSelectedId(track.id);
                      if (musicVolume === 0) {
                        setMusicVolume(DEFAULT_MUSIC_VOLUME);
                      }
                    }}
                  >
                    {track.display_name}
                  </button>
                  {durationSec != null ? (
                    <span className="studio-addons-chip-duration">
                      {formatMss(durationSec)}
                    </span>
                  ) : null}
                  <button
                    type="button"
                    className="studio-addons-chip-play"
                    data-testid={`studio-addons-preview-${track.id}`}
                    aria-label={
                      auditioning
                        ? `Stop ${track.display_name}`
                        : `Preview ${track.display_name}`
                    }
                    onClick={(event) => void togglePreview(track, event)}
                  >
                    {auditioning ? '⏹' : '▶'}
                  </button>
                </div>
              );
            })}
            {uploadsEnabled ? (
              <div className="studio-addons-chip studio-addons-chip-upload">
                <input
                  ref={musicUploadRef}
                  type="file"
                  accept="audio/*,.m4a,.mp3,.wav,.aac,.webm"
                  className="studio-addons-file"
                  data-testid="studio-addons-music-file"
                  onChange={(event) => {
                    const file = event.target.files?.[0];
                    event.target.value = '';
                    if (file) void onUploadMusic(file);
                  }}
                />
                <span className="studio-addons-chip-art is-upload" aria-hidden="true">
                  +
                </span>
                <button
                  type="button"
                  className="studio-addons-chip-pick"
                  data-testid="studio-addons-music-upload"
                  disabled={busy || !rightsAck}
                  onClick={() => musicUploadRef.current?.click()}
                >
                  {STUDIO_COPY.addonsMusicUpload}
                </button>
              </div>
            ) : null}
            {uploadedMusic ? (
              <div
                className={`studio-addons-chip ${!selectedId ? 'is-selected' : ''}`}
                data-testid="studio-addons-uploaded-music"
              >
                <span className="studio-addons-chip-art" aria-hidden="true">
                  {(job.music_asset_name || STUDIO_COPY.addonsMusicUpload)
                    .trim()
                    .charAt(0)
                    .toUpperCase() || '♪'}
                </span>
                <span className="studio-addons-chip-pick">
                  {job.music_asset_name || STUDIO_COPY.addonsMusicUpload}
                </span>
                <button
                  type="button"
                  className="studio-addons-chip-play"
                  data-testid="studio-addons-uploaded-music-remove"
                  disabled={busy}
                  onClick={() => void onRemoveUploadedMusic()}
                >
                  {STUDIO_COPY.addonsUploadedTrackRemove}
                </button>
              </div>
            ) : null}
          </div>
            </section>
          ) : null}
          {showMix ? (
            <section className={showAll ? 'studio-addons-card' : undefined}>
          <p className="studio-addons-section-label">
            {inspectorSection === 'mix'
              ? STUDIO_COPY.recapEditorV2ToolMix
              : STUDIO_COPY.addonsMix}
          </p>
          <div className="studio-addons-mix" data-testid="studio-addons-mix">
            {inspectorSection === 'mix' ? (
              <>
                <MixSlider
                  label={STUDIO_COPY.recapEditorV2GameAudio}
                  value={reelAudioVolume}
                  disabled={busy}
                  testId="studio-addons-reel-audio"
                  mutedLabel={STUDIO_COPY.recapEditorV2Muted}
                  onChange={(next) => setMixVolume({ reel_audio_volume: next })}
                />
                <MixSlider
                  label={STUDIO_COPY.recapEditorV2MusicBed}
                  value={musicVolume}
                  disabled={busy}
                  testId="studio-addons-music-volume"
                  mutedLabel={STUDIO_COPY.recapEditorV2Muted}
                  onChange={(next) => setMixVolume({ music_volume: next })}
                />
                <MixSlider
                  label={STUDIO_COPY.recapEditorV2Voiceover}
                  value={voiceVolume}
                  disabled={busy}
                  testId="studio-addons-voice-volume"
                  mutedLabel={STUDIO_COPY.recapEditorV2Muted}
                  onChange={(next) => setMixVolume({ voice_volume: next })}
                />
              </>
            ) : (
              <>
                <MixSlider
                  label={STUDIO_COPY.addonsMusicVolume}
                  value={musicVolume}
                  disabled={busy}
                  testId="studio-addons-music-volume"
                  onChange={setMusicVolume}
                />
                <MixSlider
                  label={STUDIO_COPY.addonsReelAudioVolume}
                  caption={STUDIO_COPY.musicGameVolume}
                  value={reelAudioVolume}
                  disabled={busy}
                  testId="studio-addons-reel-audio"
                  onChange={setReelAudioVolume}
                />
                <MixSlider
                  label={STUDIO_COPY.addonsVoiceVolume}
                  value={voiceVolume}
                  disabled={busy}
                  testId="studio-addons-voice-volume"
                  onChange={setVoiceVolume}
                />
              </>
            )}
          </div>
            </section>
          ) : null}
          {showVoice ? (
            <section className={showAll ? 'studio-addons-card' : undefined}>
          <p className="studio-addons-section-label">{STUDIO_COPY.addonsVoiceover}</p>
          <div className="studio-addons-voice" data-testid="studio-addons-voice">
            <button
              type="button"
              className={`studio-btn-outline${showAll ? ' studio-addons-voice-record-btn' : ''}`}
              data-testid="studio-addons-voice-record"
              disabled={busy || recordUnavailable}
              onClick={() => {
                if (recording) stopRecording();
                else void startRecording();
              }}
            >
              {recording
                ? STUDIO_COPY.addonsVoiceStop
                : hasVoice || voiceTake
                  ? STUDIO_COPY.addonsVoiceRerecord
                  : STUDIO_COPY.addonsVoiceRecord}
            </button>
            {recordUnavailable ? (
              <p
                className="studio-addons-voice-unsupported"
                data-testid="studio-addons-voice-unsupported"
              >
                {STUDIO_COPY.addonsVoiceUnsupported}
              </p>
            ) : null}
            {!dockedRecording ? (
              <label className="studio-addons-monitor">
                <input
                  type="checkbox"
                  checked={monitorEnabled}
                  disabled={busy}
                  data-testid="studio-addons-voice-monitor"
                  onChange={(event) => setMonitor(event.target.checked)}
                />
                {STUDIO_COPY.addonsVoiceMonitor}
              </label>
            ) : null}
            {inspectorSection === 'voice' && !dockedRecording ? (
              <label className="studio-addons-monitor">
                <input
                  type="checkbox"
                  checked={!monitorEnabled}
                  disabled={busy}
                  data-testid="studio-addons-voice-mute-game"
                  onChange={(event) => setMonitor(!event.target.checked)}
                />
                {STUDIO_COPY.recapEditorV2MuteGame}
              </label>
            ) : null}
            {recording && !dockedRecording ? recordMeter : null}
            {!recording && voiceTake ? (
              <div className="studio-addons-take" data-testid="studio-addons-voice-take">
                <span data-testid="studio-addons-take-length">
                  {STUDIO_COPY.addonsVoiceTakeLength.replace(
                    '{length}',
                    formatMss(voiceTake.durationSec)
                  )}
                </span>
                <button
                  type="button"
                  className={`studio-btn-outline${showAll ? ' studio-addons-pill' : ''}`}
                  data-testid="studio-addons-voice-preview"
                  disabled={busy}
                  onClick={() => {
                    if (previewing) stopPreview();
                    else void playTakePreview();
                  }}
                >
                  {previewing
                    ? STUDIO_COPY.addonsStopPreview
                    : STUDIO_COPY.addonsVoicePreview}
                </button>
                <button
                  type="button"
                  className={`studio-addons-remove-link${showAll ? ' studio-addons-pill' : ''}`}
                  data-testid="studio-addons-voice-clear"
                  disabled={busy}
                  onClick={() => void clearTake()}
                >
                  {STUDIO_COPY.addonsVoiceClearTake}
                </button>
              </div>
            ) : null}
            {uploadsEnabled ? (
              <>
                <label className="studio-addons-rights">
                  <input
                    type="checkbox"
                    checked={rightsAck}
                    data-testid="studio-addons-voice-rights"
                    onChange={(event) => setRightsAck(event.target.checked)}
                  />
                  {STUDIO_COPY.addonsVoiceRightsAck}
                </label>
                <input
                  ref={uploadRef}
                  type="file"
                  accept="audio/*,.m4a,.mp3,.wav,.aac,.webm"
                  className="studio-addons-file"
                  data-testid="studio-addons-voice-file"
                  onChange={(event) => {
                    const file = event.target.files?.[0];
                    event.target.value = '';
                    if (file) void onUploadFile(file);
                  }}
                />
                <button
                  type="button"
                  className={`studio-btn-outline${showAll ? ' studio-addons-pill' : ''}`}
                  data-testid="studio-addons-voice-upload"
                  disabled={busy || !rightsAck}
                  onClick={() => uploadRef.current?.click()}
                >
                  {STUDIO_COPY.addonsVoiceUpload}
                </button>
              </>
            ) : null}
          </div>
          {notice ? (
            <p
              className="studio-addons-notice"
              data-testid="studio-addons-upload-notice"
            >
              {notice}
            </p>
          ) : null}
            </section>
          ) : null}
          {showBranding && (watermarkToggle || inspectorSection === 'branding') ? (
            <section className={showAll ? 'studio-addons-card' : undefined}>
              {watermarkToggle}
              {inspectorSection === 'branding' ? (
                <p
                  className="recap-v2-share-note"
                  data-testid="recap-v2-share-note"
                >
                  {STUDIO_COPY.recapEditorV2ShareNote}
                </p>
              ) : null}
            </section>
          ) : null}
          {showMix ? (
          <div
            className={`studio-addons-actions${showAll ? ' studio-addons-actions-sticky' : ''}`}
          >
            <button
              type="button"
              className="studio-btn-outline"
              data-testid="studio-addons-apply"
              disabled={busy || !canApply || job.mix_status === 'applying'}
              onClick={() => void bakeMix(false)}
            >
              {busy || job.mix_status === 'applying'
                ? STUDIO_COPY.addonsApplying
                : STUDIO_COPY.addonsApply}
            </button>
            <button
              type="button"
              className="studio-btn-primary"
              data-testid="studio-addons-save-close"
              disabled={busy || !canApply || job.mix_status === 'applying'}
              onClick={() => void bakeMix(true)}
            >
              {STUDIO_COPY.addonsSaveClose}
            </button>
          <button
            type="button"
            className="studio-addons-remove-link"
            data-testid="studio-addons-remove"
            disabled={busy || !mixOn}
            onClick={() => void onRemove()}
          >
            {busy ? STUDIO_COPY.addonsRemoving : STUDIO_COPY.addonsRemove}
          </button>
          {mixOn ? (
            <p className="studio-addons-status" data-testid="studio-addons-applied">
              {STUDIO_COPY.addonsApplied}
            </p>
          ) : null}
          </div>
          ) : null}
        </>
      )}
      {workingHint && !error ? (
        <p
          className="studio-addons-notice"
          data-testid="studio-addons-still-working"
        >
          {workingHint}
        </p>
      ) : null}
      {error ? (
        <p
          className="studio-addons-error"
          role="alert"
          data-testid="studio-addons-error"
        >
          {error}
        </p>
      ) : null}
      {recordDock && dockTransport
        ? createPortal(dockTransport, recordDock)
        : null}
    </div>
  );
};

export default AddOnsPanel;
